import { create } from "zustand";
import type { SongSource } from "../types/domain";
import { clampSetting, type KeySetting } from "../core/theory/transpose";
import { keyFromSearch, songPathWithKey, videoIdFromLocation } from "../core/songUrl";
import { useProjectStore } from "./projectStore";

// Key/capo of the loaded song. Only the chords are redrawn — the recording keeps its pitch.
// A shared link's …&transpose=&capo= wins; otherwise this device remembers the last setting per song.

const STORE_PREFIX = "tutti.key.";

/** this device's storage id for a song (null for sources that are not remembered) */
export function songId(src: SongSource): string | null {
  if (src.kind === "youtube") return "yt:" + src.videoId;
  if (src.kind === "localFile") return "file:" + src.fileName;
  return null;
}

function loadSaved(id: string): KeySetting | null {
  try {
    const raw = localStorage.getItem(STORE_PREFIX + id);
    return raw ? clampSetting(JSON.parse(raw)) : null;
  } catch {
    return null;
  }
}

function save(id: string, k: KeySetting) {
  try {
    if (k.transpose || k.capo) localStorage.setItem(STORE_PREFIX + id, JSON.stringify(k));
    else localStorage.removeItem(STORE_PREFIX + id);
  } catch { /* storage blocked: the setting just isn't remembered */ }
}

// keep the address shareable (/watch?v=<id>&transpose=…&capo=…) while that song is the open page
function syncUrl(src: SongSource, k: KeySetting) {
  if (src.kind !== "youtube" || videoIdFromLocation(location.pathname, location.search) !== src.videoId) return;
  const path = songPathWithKey(src, k);
  if (location.pathname + location.search !== path) history.replaceState(history.state, "", path);
}

interface KeyState extends KeySetting {
  set: (k: Partial<KeySetting>) => void;
  reset: () => void;
}

export const useKeyStore = create<KeyState>((set, get) => ({
  transpose: 0,
  capo: 0,
  set: (k) => {
    const next = clampSetting({ transpose: get().transpose, capo: get().capo, ...k });
    set(next);
    const src = useProjectStore.getState().source;
    const id = songId(src);
    if (id) save(id, next);
    syncUrl(src, next);
  },
  reset: () => get().set({ transpose: 0, capo: 0 }),
}));

/** apply the right setting whenever a song is loaded. `onRestored` fires when it came from this device's
 *  memory (not from the link), so the UI can say so. Returns the unsubscribe. */
export function bindKeyToSong(onRestored: (k: KeySetting) => void): () => void {
  const apply = (src: SongSource) => {
    const id = songId(src);
    const onPage = src.kind === "youtube" && videoIdFromLocation(location.pathname, location.search) === src.videoId;
    const fromLink = onPage ? keyFromSearch(location.search) : null;
    const saved = !fromLink && id ? loadSaved(id) : null;
    const k = fromLink ?? saved ?? { transpose: 0, capo: 0 };
    useKeyStore.setState(k);
    syncUrl(src, k);
    if (saved) onRestored(saved);
  };
  apply(useProjectStore.getState().source);
  return useProjectStore.subscribe((s, prev) => { if (s.source !== prev.source) apply(s.source); });
}
